export const ANNOUNCEMENT_PUBLICATION_STATUS = {
  BEFORE_PUBLICATION: 'BEFORE_PUBLICATION',
  PUBLISHED: 'PUBLISHED',
  AFTER_PUBLICATION: 'AFTER_PUBLICATION',
  UNPUBLISHED: 'UNPUBLISHED',
} as const;

export type AnnouncementPublicationStatus =
  (typeof ANNOUNCEMENT_PUBLICATION_STATUS)[keyof typeof ANNOUNCEMENT_PUBLICATION_STATUS];

export const ANNOUNCEMENT_PUBLICATION_STATUS_OPTIONS: Array<{
  value: AnnouncementPublicationStatus;
  label: string;
  /** Badge variant */
  badge: 'warning' | 'success' | 'secondary' | 'outline';
}> = [
  {
    value: ANNOUNCEMENT_PUBLICATION_STATUS.BEFORE_PUBLICATION,
    label: '게시 전',
    badge: 'warning',
  },
  {
    value: ANNOUNCEMENT_PUBLICATION_STATUS.PUBLISHED,
    label: '게시 중',
    badge: 'success',
  },
  {
    value: ANNOUNCEMENT_PUBLICATION_STATUS.AFTER_PUBLICATION,
    label: '게시 종료',
    badge: 'secondary',
  },
  {
    value: ANNOUNCEMENT_PUBLICATION_STATUS.UNPUBLISHED,
    label: '미공개',
    badge: 'outline',
  },
];

const parseAnnouncementDate = (value?: string | null): Date | null => {
  if (!value) return null;
  const normalized = value.includes('T') ? value : value.replace(' ', 'T');
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const computeAnnouncementPublicationStatus = (item: {
  isPublished: boolean;
  publishedStartAt?: string;
  publishedEndAt?: string;
  publishStartAt?: string;
  publishEndAt?: string;
}): AnnouncementPublicationStatus => {
  if (!item.isPublished) {
    return ANNOUNCEMENT_PUBLICATION_STATUS.UNPUBLISHED;
  }

  const start = parseAnnouncementDate(
    item.publishedStartAt ?? item.publishStartAt,
  );
  const end = parseAnnouncementDate(item.publishedEndAt ?? item.publishEndAt);
  const now = new Date();

  if (start && now < start) {
    return ANNOUNCEMENT_PUBLICATION_STATUS.BEFORE_PUBLICATION;
  }
  if (end && now > end) {
    return ANNOUNCEMENT_PUBLICATION_STATUS.AFTER_PUBLICATION;
  }
  return ANNOUNCEMENT_PUBLICATION_STATUS.PUBLISHED;
};

export const getAnnouncementPublicationStatusLabel = (
  status: AnnouncementPublicationStatus | string,
): string =>
  ANNOUNCEMENT_PUBLICATION_STATUS_OPTIONS.find((opt) => opt.value === status)
    ?.label ?? '미공개';

export const getAnnouncementPublicationStatusBadgeVariant = (
  status: AnnouncementPublicationStatus | string,
): 'warning' | 'success' | 'secondary' | 'outline' =>
  ANNOUNCEMENT_PUBLICATION_STATUS_OPTIONS.find((opt) => opt.value === status)
    ?.badge ?? 'outline';

/** 목록 필터 — 등록 가능 카테고리 + 레거시 잔존값 */
export const ANNOUNCEMENT_CATEGORY_FILTER_OPTIONS = [
  { value: '', label: '전체 카테고리' },
  { value: 'SERVICE_UPDATE', label: '서비스 업데이트' },
  { value: 'POLICY', label: '정책' },
  { value: 'EVENT', label: '이벤트' },
  { value: 'MAINTENANCE', label: '점검' },
  { value: 'SCHEDULE_NOTICE', label: '일정 안내' },
  { value: 'DRUG_INFO', label: '의약품 정보' },
  { value: 'EVENT_PROMOTION', label: '이벤트 프로모션' },
  { value: 'IMPORTANT_NOTICE', label: '중요 공지' },
] as const;
